import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../Config/config";
import { useTenant } from "../Context/TenantContext";
import TenantLoginFooter from "./TenantLoginFooter";
import { Eye, EyeOff, KeyRound, Building2, ArrowLeft, CheckCircle2, AlertCircle } from "lucide-react";

// --- Design Tokens ------------------------------------------------------------
const T = {
  border:      "1px solid #E2E8F0",
  radius:      { sm: "8px", md: "12px", lg: "16px", xl: "20px", xxl: "24px", full: "999px" },
  shadow:      { sm: "0 2px 8px rgba(0,0,0,0.07)", lg: "0 8px 40px rgba(79, 70, 229,0.13)" },
  brand:       "var(--primary-blue, #4F46E5)",
  brandLight:  "rgba(79, 70, 229,0.08)",
  brandBorder: "rgba(79, 70, 229,0.18)",
  text: {
    heading:  "#0F172A",
    body:     "#334155",
    muted:    "#64748B",
    disabled: "#94A3B8",
  },
  bg: { main: "#F8FAFC", section: "#FFFFFF" },
};

const inputBase = {
  width: "100%", padding: "11px 44px 11px 14px",
  fontSize: "14px", fontWeight: 600, color: "#0F172A",
  background: "#F8FAFC", border: "1.5px solid #E2E8F0",
  borderRadius: "12px", outline: "none",
  transition: "border-color 0.15s, box-shadow 0.15s",
  boxSizing: "border-box",
};

// --- Password field with show/hide toggle -------------------------------------
const PasswordField = ({ id, label, value, onChange, placeholder, focused, onFocus, onBlur }) => {
  const [show, setShow] = useState(false);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      <label
        htmlFor={id}
        style={{ fontSize: "11px", fontWeight: 700, color: T.text.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}
      >
        {label}
      </label>
      <div style={{ position: "relative" }}>
        <input
          id={id}
          type={show ? "text" : "password"}
          value={value}
          required
          autoComplete={id === "newPassword" ? "new-password" : "new-password"}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder={placeholder}
          style={{
            ...inputBase,
            borderColor: focused ? T.brand : "#E2E8F0",
            boxShadow:   focused ? `0 0 0 3px ${T.brandLight}` : "none",
          }}
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          style={{
            position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)",
            background: "none", border: "none", cursor: "pointer", padding: "4px",
            color: T.text.muted, display: "flex", alignItems: "center",
          }}
          aria-label={show ? "Hide password" : "Show password"}
        >
          {show
            ? <EyeOff style={{ width: "16px", height: "16px" }} />
            : <Eye    style={{ width: "16px", height: "16px" }} />}
        </button>
      </div>
    </div>
  );
};

// --- PasswordStrengthBar ------------------------------------------------------
const strength = (pw) => {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 8)            score++;
  if (/[A-Z]/.test(pw))          score++;
  if (/[0-9]/.test(pw))          score++;
  if (/[^A-Za-z0-9]/.test(pw))  score++;
  return score;
};

const strengthMeta = [
  { label: "Too short",  color: "#EF4444" },
  { label: "Weak",       color: "#F97316" },
  { label: "Fair",       color: "#EAB308" },
  { label: "Good",       color: "#22C55E" },
  { label: "Strong",     color: "#16A34A" },
];

const StrengthBar = ({ password }) => {
  const s = strength(password);
  if (!password) return null;
  const meta = strengthMeta[s];
  return (
    <div style={{ marginTop: "6px" }}>
      <div style={{ display: "flex", gap: "4px", marginBottom: "4px" }}>
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            style={{
              flex: 1, height: "3px", borderRadius: T.radius.full,
              background: i <= s ? meta.color : "#E2E8F0",
              transition: "background 0.2s",
            }}
          />
        ))}
      </div>
      <p style={{ fontSize: "11px", fontWeight: 600, color: meta.color, margin: 0 }}>
        {meta.label}
      </p>
    </div>
  );
};

// --- ResetPassword ------------------------------------------------------------
const ResetPassword = () => {
  const { token } = useParams();
  const navigate  = useNavigate();
  const { getCollegeName, getLogoUrl } = useTenant();

  const [newPassword,     setNewPassword]     = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading,         setLoading]         = useState(false);
  const [success,         setSuccess]         = useState("");
  const [error,           setError]           = useState("");
  const [focused,         setFocused]         = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (newPassword !== confirmPassword)
      return setError("Passwords do not match.");
    if (newPassword.length < 6)
      return setError("Password must be at least 6 characters.");

    setLoading(true);
    try {
      const res = await axios.put(`${API_BASE_URL}/colleges/reset-password/${token}`, { newPassword });
      if (res.status === 200) {
        setSuccess("Password reset successfully. Redirecting to login...");
        setTimeout(() => navigate("/admin/login"), 2500);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch (err) {
      setError("Invalid or expired reset link.");
    } finally {
      setLoading(false);
    }
  };

  const collegeName = getCollegeName();
  const logoUrl     = getLogoUrl();
  const mismatch    = confirmPassword && newPassword !== confirmPassword;

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", background: T.bg.main, fontFamily: "inherit" }}>
      <main style={{
        flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
        padding: "48px 24px",
      }}>
        <div style={{ width: "100%", maxWidth: "420px" }}>

          {/* College badge */}
          <div style={{ textAlign: "center", marginBottom: "28px" }}>
            <div style={{
              width: "60px", height: "60px", borderRadius: T.radius.xl,
              background: T.bg.section, border: `2px solid ${T.brandBorder}`,
              overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 12px", boxShadow: T.shadow.sm,
            }}>
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={collegeName}
                  style={{ width: "100%", height: "100%", objectFit: "contain", padding: "6px" }}
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              ) : (
                <Building2 style={{ width: "24px", height: "24px", color: T.brand }} />
              )}
            </div>
            {collegeName && (
              <p style={{ fontSize: "14px", fontWeight: 700, color: T.text.heading, margin: 0 }}>
                {collegeName}
              </p>
            )}
          </div>

          {/* Card */}
          <div style={{
            background: T.bg.section, border: T.border,
            borderRadius: T.radius.xxl, boxShadow: T.shadow.lg, overflow: "hidden",
          }}>
            <div style={{ padding: "32px 32px 36px" }}>

              {/* Icon + heading */}
              <div style={{ marginBottom: "28px" }}>
                <div style={{
                  width: "44px", height: "44px", borderRadius: T.radius.lg,
                  background: T.brandLight, border: `1px solid ${T.brandBorder}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  marginBottom: "16px",
                }}>
                  <KeyRound style={{ width: "20px", height: "20px", color: T.brand }} />
                </div>
                <h1 style={{ fontSize: "22px", fontWeight: 800, color: T.text.heading, margin: "0 0 6px", letterSpacing: "-0.02em" }}>
                  Set new password
                </h1>
                <p style={{ fontSize: "13px", color: T.text.muted, margin: 0, fontWeight: 500, lineHeight: 1.5 }}>
                  Choose a strong password for your college account.
                </p>
              </div>

              {/* Success */}
              {success && (
                <div style={{
                  display: "flex", alignItems: "flex-start", gap: "10px",
                  padding: "12px 14px", borderRadius: T.radius.md,
                  background: "#F0FDF4", border: "1px solid #BBF7D0",
                  marginBottom: "20px",
                }}>
                  <CheckCircle2 style={{ width: "15px", height: "15px", color: "#16A34A", flexShrink: 0, marginTop: "1px" }} />
                  <p style={{ fontSize: "13px", color: "#16A34A", fontWeight: 600, margin: 0 }}>{success}</p>
                </div>
              )}

              {/* Error */}
              {error && (
                <div style={{
                  display: "flex", alignItems: "flex-start", gap: "10px",
                  padding: "12px 14px", borderRadius: T.radius.md,
                  background: "#FEF2F2", border: "1px solid #FECACA",
                  marginBottom: "20px",
                }}>
                  <AlertCircle style={{ width: "15px", height: "15px", color: "#DC2626", flexShrink: 0, marginTop: "1px" }} />
                  <p style={{ fontSize: "13px", color: "#DC2626", fontWeight: 600, margin: 0 }}>{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate style={{ display: "flex", flexDirection: "column", gap: "16px" }}>

                <div>
                  <PasswordField
                    id="newPassword"
                    label="New Password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    focused={focused === "new"}
                    onFocus={() => setFocused("new")}
                    onBlur={() => setFocused(null)}
                  />
                  <StrengthBar password={newPassword} />
                </div>

                <div>
                  <PasswordField
                    id="confirmPassword"
                    label="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your password"
                    focused={focused === "confirm"}
                    onFocus={() => setFocused("confirm")}
                    onBlur={() => setFocused(null)}
                  />
                  {mismatch && (
                    <p style={{ fontSize: "11px", color: "#EF4444", fontWeight: 600, margin: "5px 0 0" }}>
                      Passwords do not match
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || !!success || !!mismatch}
                  style={{
                    width: "100%", padding: "13px 20px", marginTop: "4px",
                    background: (loading || success || mismatch) ? "#93C5FD" : T.brand,
                    color: "#FFFFFF", border: "none", borderRadius: T.radius.md,
                    fontSize: "14px", fontWeight: 800, letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    cursor: (loading || success || mismatch) ? "not-allowed" : "pointer",
                    boxShadow: (loading || success || mismatch) ? "none" : "0 4px 16px rgba(79, 70, 229,0.32)",
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={(e) => { if (!loading && !success && !mismatch) e.currentTarget.style.opacity = "0.9"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
                >
                  {loading ? "Resetting..." : success ? "Done!" : "Reset Password"}
                </button>
              </form>
            </div>
          </div>

          {/* Back to login */}
          <button
            onClick={() => navigate("/admin/login")}
            style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
              width: "100%", marginTop: "16px", padding: "10px",
              background: "none", border: "none", cursor: "pointer",
              fontSize: "13px", fontWeight: 700, color: T.text.muted,
              transition: "color 0.15s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = T.brand; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = T.text.muted; }}
          >
            <ArrowLeft style={{ width: "14px", height: "14px" }} />
            Back to login
          </button>

        </div>
      </main>

      <TenantLoginFooter />
    </div>
  );
};

export default ResetPassword;


